import { faFilter, faPlus, faUsers } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Button from 'components/base/Button';
import SearchBox from 'components/common/SearchBox';
import CompanyFilter from 'components/common/CompanyFilter';
import UserTable from 'components/devices/UserTable';
import CreateUserModal from 'components/modals/CreateUserModal';
import { useState, ChangeEvent, useEffect, useMemo } from 'react';
import { Col, Row } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from 'store/index';
import { fetchUsers } from 'store/userSlice';
import { fetchCompanies } from 'store/companySlice';

const User = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { data: users } = useSelector((state: RootState) => state.users);
  const auth = useSelector((state: RootState) => state.auth);
  const { data: companies } = useSelector(
    (state: RootState) => state.companies
  );

  const [openFilterModal, setOpenFilterModal] = useState(false);
  const [openCreateModal, setOpenCreateModal] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCompanyId, setSelectedCompanyId] = useState<number | null>(
    null
  );
  const [showFilterPanel, setShowFilterPanel] = useState(false);
  const [selectedRole, setSelectedRole] = useState<string | null>(null);
  const [selectedStatus, setSelectedStatus] = useState<string | null>(null);

  const isSuperAdmin = useMemo(() => auth.role === 'super_user', [auth.role]);

  // Load users and companies on component mount
  useEffect(() => {
    dispatch(fetchUsers());
    if (isSuperAdmin) {
      dispatch(fetchCompanies());
    }
  }, [dispatch, isSuperAdmin]);

  const handleSearchInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
  };

  const handleCompanySelect = (companyId: number | null) => {
    setSelectedCompanyId(companyId);
  };

  return (
    <div>
      <div className="mb-6">
        <h2
          className="mb-0"
          style={{
            fontSize: '1.5rem',
            fontWeight: '600',
            color: '#374151',
            letterSpacing: '-0.025em'
          }}
        >
          <FontAwesomeIcon icon={faUsers} className="me-2 text-primary" />
          User List
        </h2>
        <Row className="g-3 justify-content-end mb-0">
          <Col xs="auto">
            <div className="d-flex gap-2">
              <button
                className="btn d-flex align-items-center gap-2"
                style={{
                  backgroundColor: 'white',
                  border: '1px solid #e5e7eb',
                  borderRadius: '0.375rem',
                  padding: '0.375rem 0.75rem',
                  fontSize: '0.875rem',
                  color: '#374151',
                  fontWeight: '400'
                }}
                onClick={() => setShowFilterPanel(!showFilterPanel)}
              >
                <FontAwesomeIcon
                  icon={faFilter}
                  style={{ fontSize: '0.875rem' }}
                />
                <span>Filter</span>
              </button>
              <SearchBox
                placeholder="Search by name"
                value={searchQuery}
                onChange={handleSearchInputChange}
              />
              <Button
                variant="primary"
                startIcon={<FontAwesomeIcon icon={faPlus} />}
                onClick={() => setOpenCreateModal(true)}
                style={{
                  fontSize: '0.875rem',
                  padding: '0.375rem 0.75rem',
                  fontWeight: '500'
                }}
              >
                Create User
              </Button>
            </div>
          </Col>
        </Row>

        {/* Filter Panel */}
        {showFilterPanel && (
          <div className="mb-3 mt-3">
            <div className="d-flex gap-2 flex-wrap">
              {isSuperAdmin && (
                <CompanyFilter
                  companies={companies}
                  selectedCompanyId={selectedCompanyId}
                  onCompanySelect={handleCompanySelect}
                />
              )}
              <select
                className="form-select"
                value={selectedRole || ''}
                onChange={e => setSelectedRole(e.target.value || null)}
                style={{
                  width: 'auto',
                  minWidth: '150px',
                  fontSize: '0.875rem',
                  padding: '0.375rem 0.75rem'
                }}
              >
                <option value="">All Roles</option>
                <option value="1">Super Admin</option>
                <option value="2">Company Admin</option>
                <option value="3">Department Head</option>
                <option value="4">User</option>
              </select>
              <select
                className="form-select"
                value={selectedStatus || ''}
                onChange={e => setSelectedStatus(e.target.value || null)}
                style={{
                  width: 'auto',
                  minWidth: '150px',
                  fontSize: '0.875rem',
                  padding: '0.375rem 0.75rem'
                }}
              >
                <option value="">All Status</option>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
              <button
                className="btn btn-outline-secondary"
                onClick={() => {
                  setSelectedCompanyId(null);
                  setSelectedRole(null);
                  setSelectedStatus(null);
                }}
                style={{
                  fontSize: '0.875rem',
                  padding: '0.375rem 0.75rem'
                }}
              >
                Clear Filters
              </button>
            </div>
          </div>
        )}

        {/* User Table */}
        <div className="mt-3">
          <UserTable
            searchQuery={searchQuery}
            selectedCompanyId={selectedCompanyId}
            selectedRole={selectedRole}
            selectedStatus={selectedStatus}
          />
        </div>

        {/* Create User Modal */}
        <CreateUserModal
          show={openCreateModal}
          handleClose={() => setOpenCreateModal(false)}
          onSuccess={() => dispatch(fetchUsers())}
        />
      </div>
    </div>
  );
};

export default User;
